import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useSearchParams } from "react-router-dom";
import { Collapse } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { MdKeyboardArrowDown, MdEdit, MdDeleteOutline, MdAddCircle, MdOutlineCheckCircle, MdOutlineRemoveCircleOutline } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import {
  Utils,
  fetchWithAuth,
  fetchAjaxListData,
  can,
  composeEventMetricLabel,
  EVENT_METRIC_OPTION,
  getDefaultPageSize,
  handleSessionExpired,
  selectStyles,
  getStatusDisplay,
  DEFAULT_STATUS_OPTIONS,
} from "../../utils";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import ListPaginationBar from "../common/ListPaginationBar";

const DEFAULT_METRIC_LABEL = EVENT_METRIC_OPTION.label; // "Revenue" — legacy default
const DEFAULT_AMOUNT_KEY = "revenue"; // matches legacy extraction: data.revenue
const MAX_METRIC_EVENT_NAMES = 20;
const METRIC_EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;

const normalizeEventName = (s) => String(s || "").trim().toLowerCase();
const isValidEventName = (s) => METRIC_EVENT_NAME_PATTERN.test(s);

const emptyForm = () => ({
  id: null,
  label: "",
  eventNames: [],
  showCount: true,
  showAmount: true,
  amountKey: "",
  status: 1,
});

/**
 * Event Metrics — per-app CRUD list (app_event_metrics table). An app can have multiple
 * event metrics (each its own name/event allow-list/show flags/amount key); every
 * dashboard/report page reads this list to render one tile/column per configured metric.
 * Replaces the old single-record "Event Metric Setting" form. Status/action-dropdown/
 * scroll-shadow follow the same pattern as AppSupportFields.js.
 */
const AppDefaultSetting = () => {
  const { appId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = getDefaultPageSize();
  const canManage = can("apps.edit");

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: "", status: "" });
  const [draftFilters, setDraftFilters] = useState({ search: "", status: "" });
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);
  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);

  const getMetricActionOptionCount = () => {
    let n = 0;
    if (canManage) n += 3; // Edit, Activate/Deactivate, Delete
    return n;
  };
  const { openActionId, openActionItem, dropdownPosition, dropdownRef, handleDropdownAction, handleOpenDropdown } = useActionDropdown({
    tableScrollRef,
    optionCount: getMetricActionOptionCount,
    menuWidth: 220,
    useAnchored: true,
  });

  const loadData = async (nextPage = page, nextFilters = filters) => {
    setLoading(true);
    const res = await fetchWithAuth(Utils.baseurl + "app-event-metrics/list", {
      method: "POST",
      body: JSON.stringify({
        id: appId,
        page: nextPage,
        pageSize,
        search: (nextFilters.search || "").trim(),
        status: nextFilters.status,
      }),
    });
    const result = await res.json().catch(() => ({}));
    setLoading(false);
    if (handleSessionExpired(res, result)) return;
    if (!result.status) {
      toast.error(result.message || "Could not load event metrics");
      return;
    }
    setRows(result.data || []);
    setMeta(result.meta || { page: 1, totalPages: 1, total: 0 });
  };

  useEffect(() => {
    if (!appId) return;
    const p = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
    const search = searchParams.get("search") || "";
    const status = searchParams.get("status") || "";
    const f = { search, status };
    setPage(p);
    setFilters(f);
    setDraftFilters(f);
    setFilterAccordionOpen(Boolean(search || status));
    loadData(p, f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, searchParams]);

  const writeParams = (p, f) => {
    const sp = new URLSearchParams();
    if (p > 1) sp.set("page", String(p));
    if ((f.search || "").trim()) sp.set("search", f.search.trim());
    if (String(f.status || "").trim()) sp.set("status", String(f.status).trim());
    setSearchParams(sp, { replace: true });
  };

  const onApplyFilters = () => {
    const f = { ...draftFilters };
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(Boolean((f.search || "").trim() || String(f.status || "").trim()));
    writeParams(1, f);
  };

  const onResetFilters = () => {
    const f = { search: "", status: "" };
    setDraftFilters(f);
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(false);
    writeParams(1, f);
  };

  // Single ajax dropdown: search events already seen for this app (via the shared /ajax-list
  // endpoint, same one every other async dropdown in the app uses), or add a custom one.
  const fetchEventMetricOptions = ({ search, ids }) =>
    fetchAjaxListData({
      type: "app-event-metrics",
      q: search,
      id: ids,
      extra: { app_id: appId },
      mode: "options",
    });

  const handleEventNamesChange = (opts) => {
    const arr = Array.isArray(opts) ? opts : [];
    const seen = new Set();
    const out = [];
    for (const o of arr) {
      const norm = normalizeEventName(o.value ?? o.label);
      if (!norm) continue;
      if (!isValidEventName(norm)) {
        toast.error("Event name: lowercase letters, numbers, underscore only");
        continue;
      }
      if (seen.has(norm)) continue;
      seen.add(norm);
      out.push(norm);
      if (out.length >= MAX_METRIC_EVENT_NAMES) {
        if (arr.length > out.length) toast.error(`Maximum ${MAX_METRIC_EVENT_NAMES} event names`);
        break;
      }
    }
    setForm((prev) => ({ ...prev, eventNames: out }));
  };

  const openAddModal = () => {
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setForm({
      id: row.id,
      label: row.label || "",
      eventNames: Array.isArray(row.event_names) ? row.event_names : [],
      showCount: row.show_count !== false,
      showAmount: row.show_amount !== false,
      amountKey: row.amount_key || "",
      status: Number(row.status) === 0 ? 0 : 1,
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.label.trim()) {
      toast.error("Metric name is required");
      return;
    }
    if (!form.showCount && !form.showAmount) {
      toast.error("Select Count or Amount (at least one is required)");
      return;
    }
    setSaving(true);
    const res = await fetchWithAuth(Utils.baseurl + "app-event-metrics/save", {
      method: "POST",
      body: JSON.stringify({
        id: appId,
        metric_id: form.id,
        label: form.label.trim(),
        event_names: form.eventNames,
        show_count: form.showCount,
        show_amount: form.showAmount,
        amount_key: form.showAmount ? form.amountKey || DEFAULT_AMOUNT_KEY : DEFAULT_AMOUNT_KEY,
        status: form.status ? 1 : 0,
      }),
    });
    const result = await res.json().catch(() => ({}));
    setSaving(false);
    if (!result.status) {
      toast.error(result.message || "Could not save event metric");
      return;
    }
    toast.success(form.id ? "Event metric updated" : "Event metric added");
    setShowModal(false);
    loadData();
  };

  const toggleMetricStatus = async (row) => {
    if (!row?.id) return;
    const nextStatus = Number(row.status) === 1 ? 0 : 1;
    const res = await fetchWithAuth(Utils.baseurl + "app-event-metrics/save", {
      method: "POST",
      body: JSON.stringify({ id: appId, metric_id: row.id, status: nextStatus }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) {
      toast.error(result.message || "Could not update event metric status");
      return;
    }
    toast.success(nextStatus === 1 ? "Event metric activated" : "Event metric deactivated");
    loadData();
  };

  const remove = async (row) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `"${row.label}" event metric will be permanently deleted.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete",
    });
    if (!confirm.isConfirmed) return;
    const res = await fetchWithAuth(Utils.baseurl + "app-event-metrics/delete", {
      method: "POST",
      body: JSON.stringify({ id: appId, metric_id: row.id }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) {
      toast.error(result.message || "Delete failed");
      return;
    }
    toast.success("Event metric deleted");
    loadData();
  };

  const shownAsFor = (row) => {
    const label = row.label || DEFAULT_METRIC_LABEL;
    const shownAs = [];
    if (row.show_count !== false) shownAs.push(composeEventMetricLabel(label, "Count"));
    if (row.show_amount !== false) shownAs.push(label);
    return shownAs;
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>Event Metrics</h1>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => loadData()}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            {canManage ? (
              <button type="button" className="add_app_btn btn_theme_primary" onClick={openAddModal}>
                <MdAddCircle />
                <span>Add Event Metric</span>
              </button>
            ) : null}
          </div>
        </div>

        <p style={{ margin: "0 0 16px", color: "#6b7280", fontSize: 13 }}>
          Choose what each event_metric is called across your dashboards and reports, which
          custom event(s) should count towards it, and whether to show its count and/or amount.
          An app can have multiple event metrics. An inactive metric is excluded everywhere (falls
          back to the default behaviour for that slot).
        </p>

        <div className="list-page-filter-card">
          <button type="button" className="list-page-filter-accordion-header" onClick={() => setFilterAccordionOpen((o) => !o)} aria-expanded={filterAccordionOpen}>
            <span className="list-page-filter-accordion-title">Filter</span>
            <MdKeyboardArrowDown className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`} size={22} />
          </button>
          <Collapse in={filterAccordionOpen}>
            <div className="list-page-filter-accordion-body">
              <div className="list-page-filter-bar">
                <div className="list-page-filter-fields row g-3 align-items-end">
                  <div className="list-page-filter-field col-12 col-lg-4">
                    <input
                      className="form-control"
                      placeholder="Search by metric name"
                      value={draftFilters.search}
                      onChange={(e) => setDraftFilters((p) => ({ ...p, search: e.target.value }))}
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-4">
                    <Select
                      inputId="event-metrics-filter-status"
                      instanceId="event-metrics-filter-status"
                      classNamePrefix="ns-event-metrics-filter-status"
                      isSearchable
                      isClearable
                      placeholder="All Status"
                      options={DEFAULT_STATUS_OPTIONS}
                      value={DEFAULT_STATUS_OPTIONS.find((o) => String(o.value) === String(draftFilters.status)) || null}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, status: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field list-page-filter-actions col-12 col-lg-12">
                    <div className="add_app">
                      <button type="button" className="add_app_btn btn_theme_primary" onClick={onApplyFilters}><span>Search</span></button>
                      <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={onResetFilters}><span>Reset</span></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="dashboard_app_table dashboard_app_table--list-records">
          <div className="dashboard_table">
            <div ref={tableScrollRef} className="table_scroll_wrapper">
              <div
                ref={tableInnerRef}
                className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
                style={{
                  maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table_outer table-sm">
                  <thead>
                    <tr>
                      <th>Metric name</th>
                      <th>Shown as</th>
                      <th>Amount key</th>
                      <th>Metric events</th>
                      <th>Status</th>
                      <th className="col-action" style={{ textAlign: "right" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="6" className="text-center">Loading...</td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan="6" className="text-center">No event metrics configured — any event with an amount counts towards the default "{DEFAULT_METRIC_LABEL}" metric.</td></tr>
                    ) : rows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.label}</td>
                        <td>{shownAsFor(row).map((s) => `"${s}"`).join(" and ") || "—"}</td>
                        <td>{row.show_amount ? (row.amount_key || DEFAULT_AMOUNT_KEY) : "—"}</td>
                        <td>
                          {Array.isArray(row.event_names) && row.event_names.length
                            ? row.event_names.join(", ")
                            : <span className="text-muted">Any event with an amount</span>}
                        </td>
                        <td>
                          {(() => {
                            const st = getStatusDisplay(row.status);
                            return <span className={st.badgeClass}>{st.label}</span>;
                          })()}
                        </td>
                        <td className="col-action" style={{ textAlign: "right" }}>
                          {canManage ? (
                            <button
                              type="button"
                              className="dropdown-trigger-btn btn btn-link p-0"
                              aria-label="Actions"
                              aria-expanded={openActionId === row.id}
                              onClick={(e) => handleOpenDropdown(e, row.id, row)}
                            >
                              <BsThreeDotsVertical />
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPaginationBar
                page={page}
                setPage={(n) => writeParams(n, filters)}
                total={meta.total || 0}
                pageSize={pageSize}
                totalPages={meta.totalPages || 1}
                ariaLabel="Event metrics pagination"
                loading={loading}
              />
            </div>
          </div>
        </div>

        {showModal ? (
          <div className="modal d-block ns-modal" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.35)" }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{form.id ? "Edit Event Metric" : "Add Event Metric"}</h5>
                  <button type="button" className="btn-close ms-auto" aria-label="Close" onClick={() => setShowModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Metric name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.label}
                      onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
                      placeholder={DEFAULT_METRIC_LABEL}
                      maxLength={64}
                      disabled={!canManage}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label d-block">Show</label>
                    <div style={{ display: "flex", gap: 20 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: canManage ? "pointer" : "default" }}>
                        <input
                          type="checkbox"
                          checked={form.showCount}
                          disabled={!canManage}
                          onChange={(e) => setForm((prev) => ({ ...prev, showCount: e.target.checked }))}
                        />
                        Count
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: canManage ? "pointer" : "default" }}>
                        <input
                          type="checkbox"
                          checked={form.showAmount}
                          disabled={!canManage}
                          onChange={(e) => setForm((prev) => ({ ...prev, showAmount: e.target.checked }))}
                        />
                        Amount
                      </label>
                    </div>
                    {!form.showCount && !form.showAmount && (
                      <p style={{ margin: "6px 0 0", fontSize: 12, color: "#dc2626" }}>Select at least one.</p>
                    )}
                  </div>

                  {form.showAmount && (
                    <div className="mb-3">
                      <label className="form-label">Amount key</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.amountKey}
                        onChange={(e) => setForm((prev) => ({ ...prev, amountKey: e.target.value }))}
                        placeholder={DEFAULT_AMOUNT_KEY}
                        maxLength={64}
                        disabled={!canManage}
                      />
                      <p style={{ margin: "6px 0 0", fontSize: 12, color: "#9ca3af" }}>
                        Which key inside the event's <code>data</code> payload holds the amount (e.g. <code>data.{form.amountKey || DEFAULT_AMOUNT_KEY}</code>).
                      </p>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Metric events</label>
                    <AsyncSearchSelect
                      inputId="event-metrics-select"
                      instanceId="event-metrics-select"
                      classNamePrefix="ns-event-metrics-select"
                      isMulti
                      creatable
                      isDisabled={!canManage}
                      fetchOptions={fetchEventMetricOptions}
                      value={form.eventNames}
                      onChange={handleEventNamesChange}
                      placeholder="Select from events already seen, or type to add a custom one…"
                      formatCreateLabel={(input) => `Add "${normalizeEventName(input)}"`}
                    />
                    <p style={{ margin: "10px 0 0", fontSize: 12, color: "#9ca3af" }}>
                      Leave empty to count any event that includes an amount value.
                      When you add event names, only those events (with an amount value) will count.
                    </p>
                  </div>

                  <div className="mb-2">
                    <label className="form-label">Status</label>
                    <Select
                      inputId="event-metrics-modal-status"
                      instanceId="event-metrics-modal-status"
                      classNamePrefix="ns-event-metrics-modal-status"
                      isSearchable={false}
                      isClearable={false}
                      options={DEFAULT_STATUS_OPTIONS}
                      value={DEFAULT_STATUS_OPTIONS.find((o) => o.value === Number(form.status)) || DEFAULT_STATUS_OPTIONS[0]}
                      onChange={(opt) => setForm((prev) => ({ ...prev, status: Number(opt?.value ?? 1) }))}
                      isDisabled={!canManage}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                    <p style={{ margin: "6px 0 0", fontSize: 12, color: "#9ca3af" }}>
                      Inactive metrics are excluded from dashboards/reports (falls back to default behaviour for that slot).
                    </p>
                  </div>
                </div>
                <div className="modal-footer">
                  {canManage ? (
                    <button type="button" className="add_app_btn btn_theme_primary" disabled={saving} onClick={save}>
                      <span>{saving ? "Saving…" : "Save"}</span>
                    </button>
                  ) : null}
                  <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={() => setShowModal(false)}><span>Close</span></button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {openActionItem && dropdownPosition
        ? createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              left: dropdownPosition.left,
              top: dropdownPosition.top,
              zIndex: 999999,
              minWidth: "170px",
            }}
          >
            <div className="dropdown-menu-subscription">
              {canManage ? (
                <button
                  type="button"
                  onClick={() => handleDropdownAction(() => openEditModal(openActionItem))}
                  style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                >
                  <MdEdit size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                  <span style={{ fontSize: "0.875rem" }}>Edit Event Metric</span>
                </button>
              ) : null}
              {canManage ? (
                <button
                  type="button"
                  onClick={() => handleDropdownAction(() => toggleMetricStatus(openActionItem))}
                  style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                >
                  {Number(openActionItem.status) === 1 ? (
                    <MdOutlineRemoveCircleOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                  ) : (
                    <MdOutlineCheckCircle size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                  )}
                  <span style={{ fontSize: "0.875rem" }}>{Number(openActionItem.status) === 1 ? "Deactivate" : "Activate"}</span>
                </button>
              ) : null}
              {canManage ? (
                <button
                  type="button"
                  className="delete-class"
                  onClick={() => handleDropdownAction(() => remove(openActionItem))}
                  style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                >
                  <MdDeleteOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                  <span style={{ fontSize: "0.875rem" }}>Delete Event Metric</span>
                </button>
              ) : null}
            </div>
          </div>,
          document.body
        )
        : null}
    </div>
  );
};

export default AppDefaultSetting;
